import { EditOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import { dateValue } from "../../common/columns";
import { NoData } from "../../common/components/NoDataComp";
import { PageTitleContainer } from "../../common/components/PageTitle";
import { projectActions } from "../../features/project/projectSlice";
import ConnectedAppLayout from "../AppLayout";
import { routeNames } from "../common/routes";
import type {
  CurrentUser,
  ProjectDetailed,
  Service,
  Turbine,
  UserLimitedList,
} from "../generated-axios-client";
import {
  PROJECT_STATUS_COLORS,
  PROJECT_STATUS_LABELS,
} from "../project/projectConstants";
import type { RootState } from "../store";
import { ProjectWorkOrdersTable } from "./ProjectWorkOrdersTable";

type ProjectViewPageProps = {
  intl: IntlShape;
  project: ProjectDetailed | null;
  modalApi: HookAPI | undefined;
  serviceObjects: Record<string, Service> | null;
  turbines: Turbine[] | undefined;
  technicians: UserLimitedList[];
  user: CurrentUser | undefined;
} & DispatchProp;

export const ProjectViewPage = ({
  intl,
  project,
  dispatch,
  modalApi,
  serviceObjects,
  turbines,
  technicians,
  user,
}: ProjectViewPageProps) => {
  if (!project) {
    return (
      <ConnectedAppLayout>
        <Skeleton active />
      </ConnectedAppLayout>
    );
  }

  const showDeleteConfirm = () => {
    modalApi?.confirm({
      title: intl.formatMessage({ id: "project_delete_confirmation" }),
      icon: <ExclamationCircleFilled />,
      okText: intl.formatMessage({ id: "confirm" }),
      okType: "danger",
      cancelText: intl.formatMessage({ id: "cancel" }),
      onOk() {
        if (project.id) {
          dispatch(projectActions.deleteProject(project.id));
        }
      },
      onCancel() {},
    });
  };

  const handleMarkDelayed = () => {
    if (!project.id) return;
    dispatch(projectActions.markProjectDelayed(project.id));
  };

  const handleResume = () => {
    if (!project.id) return;
    dispatch(projectActions.resumeProject(project.id));
  };

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={project.name ?? "—"}
        extra={
          <Space>
            {project.project_status === "ACTIVE" && (
              <Button onClick={handleMarkDelayed}>Mark as Delayed</Button>
            )}
            {project.project_status === "DELAYED" && (
              <Button onClick={handleResume}>Resume Project</Button>
            )}
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                dispatch(
                  routerActions.navigateTo(routeNames.PROJECT_EDIT, {
                    id: project.id,
                  })
                )
              }
            >
              {intl.formatMessage({ id: "project_edit_title" })}
            </Button>
            <Button danger onClick={showDeleteConfirm}>
              {intl.formatMessage({ id: "project_delete_button" })}
            </Button>
          </Space>
        }
      >
        <Flex vertical gap="middle">
          <Card title={intl.formatMessage({ id: "project_section_details" })}>
            <Descriptions bordered column={2}>
              <Descriptions.Item
                label={intl.formatMessage({ id: "project_name" })}
                span={2}
              >
                {project.name ?? <NoData intl={intl} />}
              </Descriptions.Item>
              <Descriptions.Item
                label={intl.formatMessage({ id: "project_status" })}
                span={2}
              >
                {project.project_status &&
                project.project_status in PROJECT_STATUS_LABELS ? (
                  <Tag
                    color={
                      project.project_status in PROJECT_STATUS_COLORS
                        ? PROJECT_STATUS_COLORS[project.project_status]
                        : "gray"
                    }
                    variant="outlined"
                    styles={{ root: { fontWeight: 700 } }}
                  >
                    {PROJECT_STATUS_LABELS[project.project_status]}
                  </Tag>
                ) : (
                  <NoData intl={intl} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={intl.formatMessage({ id: "project_start_date" })}>
                {project.start ? dateValue(project.start, intl) : <NoData intl={intl} />}
              </Descriptions.Item>
              <Descriptions.Item label={intl.formatMessage({ id: "project_end_date" })}>
                {project.end ? (
                  (() => {
                    const isOverdue =
                      !["COMPLETED", "DELAYED"].includes(project.project_status ?? "") &&
                      new Date(project.end) < new Date();
                    return isOverdue ? (
                      <Typography.Text type="danger">
                        {dateValue(project.end, intl)}
                      </Typography.Text>
                    ) : (
                      dateValue(project.end, intl)
                    );
                  })()
                ) : (
                  <NoData intl={intl} />
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={intl.formatMessage({ id: "project_client" })}
                span={2}
              >
                {project.client ? (
                  <Typography.Link
                    onClick={() =>
                      dispatch(
                        routerActions.navigateTo(routeNames.CLIENT_VIEW, {
                          id: project.client.id,
                        })
                      )
                    }
                  >
                    {project.client.legal_name}
                  </Typography.Link>
                ) : (
                  <NoData intl={intl} />
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={intl.formatMessage({
                  id: "project_table_column_created_at",
                })}
              >
                {project.created_at ? (
                  dateValue(project.created_at, intl)
                ) : (
                  <NoData intl={intl} />
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={intl.formatMessage({ id: "table_column_last_updated" })}
              >
                {project.updated_at ? (
                  dateValue(project.updated_at, intl)
                ) : (
                  <NoData intl={intl} />
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title={intl.formatMessage({ id: "work_orders" })}>
            <ProjectWorkOrdersTable
              tickets={project.tickets}
              projectId={project.id ?? ""}
              intl={intl}
              dispatch={dispatch}
              serviceObjects={serviceObjects}
              turbines={turbines}
              technicians={technicians}
              user={user}
              modalApi={modalApi}
            />
          </Card>
        </Flex>
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
};

export const ConnectedProjectViewPage = connect((state: RootState) => ({
  intl: state.intl.datetimeIntl,
  project: state.project.project,
  modalApi: state.appFeedback.modal,
  serviceObjects: state.ticket.servicesObj,
  turbines: state.ticketData.turbines,
  technicians: state.ticketData.technicians ?? [],
  user: state.userstate.user,
}))(ProjectViewPage);

export default ConnectedProjectViewPage;
